'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Brain, Zap, ChevronRight, CheckCircle, X, Clock, Sparkles, ArrowRight } from 'lucide-react';
import { ReactionAttentionTest } from './BrainCheckTests';
import {
  computeBrainCheckScores,
  computeDeltas,
  computeConfidence,
  deriveCognitiveState,
  generateStaticInsight,
  saveBrainCheckSession,
  getBrainCheckBaseline,
  type BrainCheckRawMetrics,
  type BrainCheckResult,
} from '@/lib/services/brainCheckService';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

type DailyCheckPhase = 'intro' | 'measuring' | 'micro_task' | 'computing' | 'insight' | 'action';

interface DailyBrainCheckProps {
  onClose?: () => void;
  onComplete?: (result: BrainCheckResult) => void;
  eegAvailable?: boolean;
  hrvScore?: number;
  sleepScore?: number;
}

// ─── Micro-task: 10-second reaction burst ────────────────────────────────────

function MicroReactionTask({ onComplete }: { onComplete: (score: number, rt: number) => void }) {
  const [phase, setPhase] = useState<'ready' | 'waiting' | 'go' | 'done'>('ready');
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [trial, setTrial] = useState(0);
  const stimTime = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const TOTAL_TRIALS = 6;

  const runTrial = useCallback((trialIdx: number) => {
    if (trialIdx >= TOTAL_TRIALS) {
      setPhase('done');
      return;
    }
    setPhase('waiting');
    const delay = 600 + Math.random() * 1200;
    timerRef.current = setTimeout(() => {
      setPhase('go');
      stimTime.current = Date.now();
      timerRef.current = setTimeout(() => {
        setMisses(m => m + 1);
        setTrial(t => {
          const next = t + 1;
          setTimeout(() => runTrial(next), 300);
          return next;
        });
        setPhase('waiting');
      }, 800);
    }, delay);
  }, []);

  useEffect(() => {
    if (phase === 'done') {
      const avgRt = reactionTimes.length > 0
        ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length
        : 600;
      const accuracy = hits / TOTAL_TRIALS;
      const rtScore = Math.max(0, Math.min(100, ((800 - avgRt) / 600) * 100));
      const score = Math.round(accuracy * 60 + rtScore * 40);
      onComplete(score, Math.round(avgRt));
    }
  }, [phase]);

  const handleTap = () => {
    if (phase !== 'go') return;
    if (timerRef.current) clearTimeout(timerRef.current);
    const rt = Date.now() - stimTime.current;
    setReactionTimes(prev => [...prev, rt]);
    setHits(h => h + 1);
    const next = trial + 1;
    setTrial(next);
    setPhase('waiting');
    setTimeout(() => runTrial(next), 400);
  };

  const progress = (trial / TOTAL_TRIALS) * 100;

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="text-[12px] text-[#8BAFD4] text-center">Tap when you see <span className="text-emerald-400 font-bold">GO</span></p>

      {/* Progress */}
      <div className="w-full h-1 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
        <div className="h-full bg-[#F97316] rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      {/* Stimulus */}
      <button
        onClick={handleTap}
        className="relative flex items-center justify-center rounded-full transition-all duration-100 active:scale-95 select-none"
        style={{
          width: 'min(120px, 38vw)',
          height: 'min(120px, 38vw)',
          background: phase === 'go' ? 'rgba(16,185,129,0.85)' : phase === 'waiting' ? 'rgba(30,58,95,0.3)' : 'rgba(30,58,95,0.2)',
          border: phase === 'go' ? '3px solid rgba(16,185,129,0.9)' : '2px solid rgba(30,58,95,0.4)',
          boxShadow: phase === 'go' ? '0 0 28px rgba(16,185,129,0.5)' : 'none',
        }}
      >
        {phase === 'ready' && <span className="text-[12px] text-[#8BAFD4]">Ready</span>}
        {phase === 'waiting' && <div className="w-2 h-2 rounded-full bg-[#1E3A5F]/60 animate-pulse" />}
        {phase === 'go' && <span className="text-white text-[18px] font-bold">GO!</span>}
        {phase === 'done' && <CheckCircle size={28} className="text-emerald-400" />}
      </button>

      {phase === 'ready' && (
        <button onClick={() => runTrial(0)}
          className="px-6 py-2.5 rounded-xl bg-[#F97316] text-white text-[13px] font-bold transition-all active:scale-95">
          Start
        </button>
      )}

      <div className="flex gap-4 text-center text-[11px]">
        <div><span className="text-emerald-400 font-bold">{hits}</span> <span className="text-[#8BAFD4]">hits</span></div>
        <div><span className="text-red-400 font-bold">{misses}</span> <span className="text-[#8BAFD4]">missed</span></div>
        <div><span className="text-[#F97316] font-bold">{trial}/{TOTAL_TRIALS}</span> <span className="text-[#8BAFD4]">trials</span></div>
      </div>
    </div>
  );
}

// ─── Insight card ─────────────────────────────────────────────────────────────

function DailyInsightCard({ result }: { result: BrainCheckResult }) {
  const { scores, cognitiveState, insight, recommendation, deltas } = result;

  const stateEmoji: Record<string, string> = {
    Focused: '🎯', Elevated: '🚀', Balanced: '⚖️',
    Recovering: '🌱', Fatigued: '😴', Stressed: '⚡',
    focused: '🎯', elevated: '🚀', balanced: '⚖️',
    recovering: '🌱', fatigued: '😴', stressed: '⚡',
  };

  const stateColor: Record<string, string> = {
    Focused: '#10B981', Elevated: '#F97316', Balanced: '#8B5CF6',
    Recovering: '#38BDF8', Fatigued: '#F59E0B', Stressed: '#F43F5E',
    focused: '#10B981', elevated: '#F97316', balanced: '#8B5CF6',
    recovering: '#38BDF8', fatigued: '#F59E0B', stressed: '#F43F5E',
  };

  const label = cognitiveState.label;
  const color = stateColor[label] ?? '#8B5CF6';
  const emoji = stateEmoji[label] ?? '🧠';

  return (
    <div className="space-y-3">
      {/* State */}
      <div className="rounded-2xl p-4 flex items-center gap-3 border"
        style={{ background: `${color}10`, borderColor: `${color}25` }}>
        <span className="text-3xl">{emoji}</span>
        <div>
          <p className="text-[11px] text-[#8BAFD4] uppercase tracking-wider">Your brain state</p>
          <p className="text-[17px] font-bold" style={{ color }}>{label}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-[11px] text-[#8BAFD4]">Overall</p>
          <p className="text-[20px] font-bold text-white">{scores.overall}</p>
        </div>
      </div>

      {/* Insight */}
      <div className="rounded-xl border border-[#1E3A5F]/50 bg-[#0B1628]/80 p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-[#38BDF8]/15 flex items-center justify-center flex-shrink-0">
          <Sparkles size={13} className="text-[#38BDF8]" />
        </div>
        <p className="text-[12px] text-zinc-300 leading-relaxed">{insight}</p>
      </div>

      {/* Recommended action */}
      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
          <ArrowRight size={13} className="text-emerald-400" />
        </div>
        <div>
          <p className="text-[10px] text-emerald-400/70 uppercase tracking-wider mb-1 font-medium">Recommended action</p>
          <p className="text-[12px] text-zinc-300 leading-relaxed">{recommendation}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function DailyBrainCheck({
  onClose,
  onComplete,
  eegAvailable = false,
  hrvScore,
  sleepScore,
}: DailyBrainCheckProps) {
  const { user } = useAuth();
  const [phase, setPhase] = useState<DailyCheckPhase>('intro');
  const [result, setResult] = useState<BrainCheckResult | null>(null);
  const [microScore, setMicroScore] = useState<number | null>(null);
  const [microRt, setMicroRt] = useState<number | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const startTime = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rawMetrics = useRef<Partial<BrainCheckRawMetrics>>({ completedTests: [] });

  // Timer
  useEffect(() => {
    if (phase === 'measuring' || phase === 'micro_task') {
      startTime.current = startTime.current || Date.now();
      timerRef.current = setInterval(() => {
        setElapsedSec(Math.round((Date.now() - startTime.current) / 1000));
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase]);

  const handleStart = () => {
    startTime.current = Date.now();
    rawMetrics.current = { completedTests: [] };
    setPhase('measuring');
  };

  const handleMeasureComplete = useCallback((metrics: any) => {
    rawMetrics.current.reaction = metrics;
    rawMetrics.current.completedTests = ['reaction'];
    setPhase('micro_task');
  }, []);

  const handleMicroTaskComplete = useCallback(async (score: number, rt: number) => {
    setMicroScore(score);
    setMicroRt(rt);
    rawMetrics.current.durationMs = Date.now() - startTime.current;
    setPhase('computing');

    const raw = rawMetrics.current as BrainCheckRawMetrics;
    const scores = computeBrainCheckScores(raw);
    const confidence = computeConfidence(raw);
    const cognitiveState = deriveCognitiveState(scores, eegAvailable, hrvScore, sleepScore);
    const { insight, recommendation } = generateStaticInsight(scores, cognitiveState);

    let deltas;
    if (user) {
      const baseline = await getBrainCheckBaseline(user.id);
      if (baseline) deltas = computeDeltas(scores, baseline);
    }

    const finalResult: BrainCheckResult = {
      scores,
      deltas,
      confidence,
      cognitiveState,
      insight,
      recommendation,
      rawMetrics: raw,
      timestamp: new Date().toISOString(),
    };

    if (user) {
      const sessionId = await saveBrainCheckSession(user.id, finalResult);
      if (sessionId) finalResult.sessionId = sessionId;
    }

    setResult(finalResult);
    setTimeout(() => setPhase('insight'), 800);
    onComplete?.(finalResult);
  }, [user, eegAvailable, hrvScore, sleepScore, onComplete]);

  const totalEstimated = 75; // 60-90 second target
  const progressPct = Math.min((elapsedSec / totalEstimated) * 100, 100);

  return (
    <div className="fixed inset-0 z-50 bg-[#060E1A]/96 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-sm bg-[#0B1628] border border-[#1E3A5F]/80 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1E3A5F]/50">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl bg-[#F97316]/15 flex items-center justify-center">
              <Brain size={14} className="text-[#F97316]" />
            </div>
            <span className="text-[13px] font-bold text-white">Daily Brain Check</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F97316]/15 text-[#F97316] border border-[#F97316]/25 font-semibold">
              ~75s
            </span>
          </div>
          {onClose && (
            <button onClick={onClose}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-500 hover:text-white hover:bg-[#1E3A5F]/50 transition-all">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Progress bar (during active phases) */}
        {(phase === 'measuring' || phase === 'micro_task') && (
          <div className="px-5 pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-[#8BAFD4]">
                {phase === 'measuring' ? 'Quick measurement' : 'Micro-training'}
              </span>
              <span className="text-[10px] text-[#8BAFD4] flex items-center gap-1">
                <Clock size={9} /> {elapsedSec}s
              </span>
            </div>
            <div className="h-1 bg-[#1E3A5F]/40 rounded-full overflow-hidden">
              <div className="h-full bg-[#F97316] rounded-full transition-all duration-1000"
                style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[75vh]">
          {/* Intro */}
          {phase === 'intro' && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#F97316]/15 border border-[#F97316]/30 flex items-center justify-center mx-auto mb-3">
                  <Brain size={28} className="text-[#F97316]" />
                </div>
                <h3 className="text-[16px] font-bold text-white mb-1">Good to see you</h3>
                <p className="text-[12px] text-zinc-400">Your daily 75-second brain check is ready</p>
              </div>

              <div className="space-y-2">
                {[
                  { icon: <Zap size={13} />, label: 'Quick measurement', desc: '~30s reaction check', color: '#F97316' },
                  { icon: <Brain size={13} />, label: 'Micro-training', desc: '~30s focused task', color: '#8B5CF6' },
                  { icon: <Sparkles size={13} />, label: 'Your insight', desc: 'Personalised to today', color: '#38BDF8' },
                  { icon: <ArrowRight size={13} />, label: 'Recommended action', desc: 'One thing to do next', color: '#10B981' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-[#060E1A]/60 border border-[#1E3A5F]/40">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${step.color}15`, color: step.color }}>
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-[12px] font-semibold text-white">{step.label}</p>
                      <p className="text-[10px] text-zinc-500">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={handleStart}
                className="w-full py-4 rounded-2xl bg-[#F97316] hover:bg-[#EA6C0A] text-white font-bold text-[14px] transition-all active:scale-98 flex items-center justify-center gap-2 min-h-[52px]">
                Start Daily Check <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* Measuring: quick reaction test */}
          {phase === 'measuring' && (
            <div className="space-y-3">
              <p className="text-[12px] text-[#8BAFD4] text-center">Quick measurement — reaction & attention</p>
              <ReactionAttentionTest onComplete={handleMeasureComplete} />
            </div>
          )}

          {/* Micro-task */}
          {phase === 'micro_task' && (
            <div className="space-y-3">
              <p className="text-[12px] text-[#8BAFD4] text-center">Micro-training — reaction speed burst</p>
              <MicroReactionTask onComplete={handleMicroTaskComplete} />
            </div>
          )}

          {/* Computing */}
          {phase === 'computing' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="w-12 h-12 border-2 border-[#F97316]/30 border-t-[#F97316] rounded-full animate-spin" />
              <p className="text-[13px] text-zinc-400">Generating your insight…</p>
            </div>
          )}

          {/* Insight */}
          {phase === 'insight' && result && (
            <div className="space-y-4">
              <DailyInsightCard result={result} />
              <button onClick={() => setPhase('action')}
                className="w-full py-3.5 rounded-2xl bg-[#F97316] text-white font-bold text-[13px] transition-all active:scale-98 flex items-center justify-center gap-2 min-h-[48px]">
                See recommended action <ChevronRight size={14} />
              </button>
            </div>
          )}

          {/* Action */}
          {phase === 'action' && result && (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle size={32} className="text-emerald-400 mx-auto mb-2" />
                <p className="text-[15px] font-bold text-white">Daily check complete</p>
                <p className="text-[11px] text-zinc-500 mt-1">See you tomorrow — consistency is the key</p>
              </div>

              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                  <ArrowRight size={13} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-[10px] text-emerald-400/70 uppercase tracking-wider mb-1 font-medium">Your action for today</p>
                  <p className="text-[13px] text-zinc-300 leading-relaxed">{result.recommendation}</p>
                </div>
              </div>

              {/* Streak reminder */}
              <div className="rounded-xl border border-[#1E3A5F]/40 bg-[#0B1628]/60 p-3 flex items-center gap-3">
                <span className="text-2xl">🔥</span>
                <div>
                  <p className="text-[12px] font-semibold text-white">Come back tomorrow</p>
                  <p className="text-[10px] text-zinc-500">Daily checks build your cognitive baseline over time</p>
                </div>
              </div>

              {onClose && (
                <button onClick={onClose}
                  className="w-full py-3.5 rounded-2xl border border-[#1E3A5F]/60 text-zinc-400 hover:text-white text-[13px] font-medium transition-all min-h-[48px]">
                  Done
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
